import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../../api/client";
import { useCars } from "../../api/cars";
import {
  useFuelImport,
  useValidateFuelImport,
  type FuelImportInput,
} from "../../api/mutations";
import type { FuelImportSummary } from "../../api/types";
import Modal from "../../components/Modal";

interface ImportFuelCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultCarId?: number | null;
}

type Step = "form" | "confirm" | "done";

export default function ImportFuelCsvModal({
  isOpen,
  onClose,
  defaultCarId,
}: ImportFuelCsvModalProps) {
  const cars = useCars();
  const validateImport = useValidateFuelImport();
  const importFuel = useFuelImport();

  const editableCars = useMemo(
    () => (cars.data?.cars ?? []).filter((car) => car.access_level !== "viewer"),
    [cars.data?.cars],
  );

  // Keep a ref so the reset effect can read the latest editableCars without
  // having it as a dependency — prevents the effect re-running (and wiping
  // the "done" step) when the import mutation invalidates the cars query.
  const editableCarsRef = useRef(editableCars);
  editableCarsRef.current = editableCars;

  const [carId, setCarId] = useState<number | "">("");
  const [fuelCsv, setFuelCsv] = useState<File | null>(null);
  const [odometerCsv, setOdometerCsv] = useState<File | null>(null);
  const [step, setStep] = useState<Step>("form");
  const [summary, setSummary] = useState<FuelImportSummary | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // Set when a native file-picker is open. Some browsers fire a dialog-level
  // cancel event when the picker is dismissed, which would incorrectly close
  // the modal. We suppress that close until the picker is resolved.
  const filePickerOpenRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setCarId(() => {
      const cars = editableCarsRef.current;
      if (defaultCarId && cars.some((c) => c.id === defaultCarId)) return defaultCarId;
      return cars[0]?.id || "";
    });
    setFuelCsv(null);
    setOdometerCsv(null);
    setStep("form");
    setSummary(null);
    setErrors([]);
    filePickerOpenRef.current = false;
  }, [defaultCarId, isOpen]);

  function trackFilePickerOpen() {
    filePickerOpenRef.current = true;
    // Safety valve: clear after 5 s in case the browser doesn't fire the
    // input's cancel event (e.g. older mobile WebKit).
    setTimeout(() => { filePickerOpenRef.current = false; }, 5000);
  }

  function handleClose() {
    if (filePickerOpenRef.current) {
      // File picker was just dismissed — don't close the modal.
      filePickerOpenRef.current = false;
      return;
    }
    if (step !== "done" && (fuelCsv || odometerCsv)) {
      if (!window.confirm("Close? Your selected files will be lost.")) return;
    }
    onClose();
  }

  function input(): FuelImportInput | null {
    if (!carId) {
      setErrors(["Select a car."]);
      return null;
    }
    if (!fuelCsv) {
      setErrors(["Choose a fuel CSV file."]);
      return null;
    }
    return { carId, fuelCsv, odometerCsv };
  }

  function errorsFrom(err: unknown, fallback: string): string[] {
    if (err instanceof ApiError) {
      if (err.errors) return Object.entries(err.errors).map(([key, value]) => `${key}: ${value}`);
      return [err.message];
    }
    return [fallback];
  }

  async function handleValidate() {
    setErrors([]);
    setSummary(null);
    const payload = input();
    if (!payload) return;
    try {
      const result = await validateImport.mutateAsync(payload);
      setSummary(result.summary);
      setStep("confirm");
    } catch (err) {
      setErrors(errorsFrom(err, "Could not validate the CSV files."));
    }
  }

  async function handleImport() {
    setErrors([]);
    const payload = input();
    if (!payload) return;
    try {
      const result = await importFuel.mutateAsync(payload);
      setSummary(result.summary);
      setStep("done");
    } catch (err) {
      setStep("form");
      setErrors(errorsFrom(err, "Could not import the CSV files."));
    }
  }

  const busy = validateImport.isPending || importFuel.isPending;
  const title = step === "confirm" ? "Are you sure?" : "Import fuel records";

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => undefined : handleClose} title={title}>
      {step === "form" ? (
        <form
          className="import-form"
          onSubmit={(event) => {
            event.preventDefault();
            void handleValidate();
          }}
        >
          <label className="form-row">
            <span className="form-row__label">Car</span>
            <select
              className="form-row__input"
              value={carId}
              onChange={(event) => setCarId(Number(event.target.value))}
              disabled={busy || editableCars.length === 0}
            >
              {editableCars.map((car) => (
                <option key={car.id} value={car.id}>
                  {car.name}
                </option>
              ))}
            </select>
          </label>
          <label className="form-row">
            <span className="form-row__label">Fuel CSV</span>
            <input
              className="form-row__input"
              type="file"
              accept=".csv,text/csv"
              required
              onClick={trackFilePickerOpen}
              onChange={(event) => {
                filePickerOpenRef.current = false;
                setFuelCsv(event.target.files?.[0] ?? null);
              }}
              disabled={busy}
            />
          </label>
          <label className="form-row">
            <span className="form-row__label">Odometer CSV optional</span>
            <input
              className="form-row__input"
              type="file"
              accept=".csv,text/csv"
              onClick={trackFilePickerOpen}
              onChange={(event) => {
                filePickerOpenRef.current = false;
                setOdometerCsv(event.target.files?.[0] ?? null);
              }}
              disabled={busy}
            />
          </label>
          <p className="section-help">
            Required fuel columns: datetime, amount_eur, liters, fuel_type,
            station_name, city, country. Optional: is_full_tank, notes, odometer_km.
          </p>
          {errors.length > 0 ? <ErrorList errors={errors} /> : null}
          <div className="form-actions form-actions--inline">
            <button type="button" className="button button--ghost" onClick={handleClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="button button--primary" disabled={busy || editableCars.length === 0}>
              Validate
            </button>
          </div>
        </form>
      ) : null}

      {step === "confirm" && summary ? (
        <div className="import-form">
          <p className="confirm-modal__body">
            Import {summary.fuel_entries} fuel record{summary.fuel_entries === 1 ? "" : "s"}
            {summary.odometer_entries > 0
              ? ` and ${summary.odometer_entries} odometer reading${summary.odometer_entries === 1 ? "" : "s"}`
              : ""}
            ?
          </p>
          {summary.warnings.length > 0 ? <WarningList warnings={summary.warnings} /> : null}
          {errors.length > 0 ? <ErrorList errors={errors} /> : null}
          <div className="form-actions form-actions--inline">
            <button type="button" className="button button--ghost" onClick={() => setStep("form")} disabled={busy}>
              Back
            </button>
            <button type="button" className="button button--primary" onClick={() => void handleImport()} disabled={busy}>
              Import
            </button>
          </div>
        </div>
      ) : null}

      {step === "done" && summary ? (
        <div className="import-form">
          <p className="form-success">
            Imported {summary.fuel_entries} fuel record{summary.fuel_entries === 1 ? "" : "s"}
            {summary.odometer_entries > 0
              ? ` and ${summary.odometer_entries} odometer reading${summary.odometer_entries === 1 ? "" : "s"}`
              : ""}
            .
          </p>
          <div className="form-actions form-actions--inline">
            <button type="button" className="button button--primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function ErrorList({ errors }: { errors: string[] }) {
  return (
    <div className="import-issues" data-variant="error">
      <p className="import-issues__title">Fix these before importing:</p>
      <ul>
        {errors.map((error) => (
          <li key={error}>{error}</li>
        ))}
      </ul>
    </div>
  );
}

function WarningList({ warnings }: { warnings: string[] }) {
  return (
    <div className="import-issues" data-variant="warning">
      <p className="import-issues__title">Warnings:</p>
      <ul>
        {warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}
